/**
 * <newsletter-popup>
 *
 * Popup newsletter avec teaser flottant (style chat inbox) et/ou déclencheurs
 * auto (délai / scroll / exit). Indépendant du bloc `popup` du thème.
 *
 * Réutilise le système de modale natif du thème : <dialog> + classes
 * `dialog-modal` / `dialog-modal--closing` d'assets/base.css.
 *
 * Fichier hors référentiel FullStack : il survit aux mises à jour du thème.
 */

const STORAGE_PREFIX = 'fs:newsletter-popup:';
const MOBILE_BREAKPOINT = 750;
const BUSY_RETRY_DELAY = 1500;
const BUSY_MAX_RETRIES = 4;

class NewsletterPopup extends HTMLElement {
  #timers = [];
  #listeners = []; // permanents : modale, formulaire, éditeur — vidés au disconnect
  #triggerListeners = []; // armables : délai, scroll, sortie — vidés à l'ouverture
  #armed = false;
  #opened = false;
  #busyRetries = 0;
  #closeFallback = null;
  #teaserDismissed = false;

  connectedCallback() {
    this.dialog = this.querySelector('[data-ref="popup-modal"]');
    if (!this.dialog) return;

    this.teaser = this.querySelector('[data-ref="popup-teaser"]');
    this.teaserOpen = this.querySelector('[data-ref="popup-teaser-open"]');
    this.teaserClose = this.querySelector('[data-ref="popup-teaser-close"]');
    this.closeButtons = this.querySelectorAll('[data-ref="popup-close"]');
    this.form = this.querySelector('.newsletter-popup__form');

    this.key = STORAGE_PREFIX + (this.dataset.key || 'default');
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.showTeaser = this.dataset.showTeaser === 'true';

    this.#bindDialog();
    this.#bindForm();
    this.#bindTeaser();
    this.#bindDesignMode();

    // Retour après envoi natif Shopify (même flux que le bloc newsletter-signup)
    if (this.#handleNativeFormResponse()) return;

    if (!this.#shouldShow()) return;

    if (this.showTeaser) this.#showTeaser();

    // Ouverture auto uniquement sans teaser et avec déclencheurs activés
    if (!this.showTeaser) this.#armTriggers();
  }

  disconnectedCallback() {
    this.#teardown();
    this.#releaseScroll();
  }

  /* ------------------------------------------------------------------ État */

  #read() {
    try {
      return JSON.parse(localStorage.getItem(this.key) || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  #write(patch) {
    try {
      localStorage.setItem(this.key, JSON.stringify({ ...this.#read(), ...patch }));
    } catch (error) {
      /* navigation privée / stockage refusé : le popup reste fonctionnel */
    }
  }

  #sessionSeen(value) {
    try {
      if (value === undefined) return sessionStorage.getItem(this.key + ':seen') === '1';
      sessionStorage.setItem(this.key + ':seen', value ? '1' : '0');
    } catch (error) {
      return false;
    }
    return false;
  }

  #teaserSessionDismissed(value) {
    try {
      if (value === undefined) return sessionStorage.getItem(this.key + ':teaser-dismissed') === '1';
      sessionStorage.setItem(this.key + ':teaser-dismissed', value ? '1' : '0');
    } catch (error) {
      return false;
    }
    return false;
  }

  #matchesDevice() {
    const display = this.dataset.display || 'desktop_and_mobile';
    if (display === 'desktop_and_mobile') return true;
    const isDesktop = window.innerWidth > MOBILE_BREAKPOINT;
    return display === 'desktop_only' ? isDesktop : !isDesktop;
  }

  #shouldShow() {
    if (!this.#matchesDevice()) return false;
    if (this.designMode) return true; // aperçu marchand : on ignore la persistance

    const state = this.#read();

    if (this.dataset.hideAfterSubscribe === 'true' && state.subscribed) return false;

    const frequency = this.dataset.frequency || 'days';
    if (frequency === 'always') return true;

    // Teaser : masqué pour la session si la croix a été cliquée
    if (this.showTeaser) return !this.#teaserSessionDismissed();

    // Mode auto sans teaser : jamais deux fois dans la même session
    if (this.#sessionSeen()) return false;

    if (frequency === 'session') return true;
    if (frequency === 'once') return !state.closedAt;

    if (!state.closedAt) return true;
    const days = parseInt(this.dataset.days || '14', 10);
    return Date.now() - state.closedAt > days * 86400000;
  }

  /* ---------------------------------------------------------------- Teaser */

  #bindTeaser() {
    if (!this.teaser) return;

    if (this.teaserOpen) {
      this.#on(this.teaserOpen, 'click', (event) => {
        event.preventDefault();
        this.open();
      });
    }

    if (this.teaserClose) {
      this.#on(this.teaserClose, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.#dismissTeaser();
      });
    }
  }

  #showTeaser() {
    if (!this.teaser || this.#teaserDismissed) return;
    this.teaser.hidden = false;
  }

  #hideTeaser() {
    if (!this.teaser) return;
    this.teaser.hidden = true;
  }

  #dismissTeaser() {
    this.#teaserDismissed = true;
    this.#hideTeaser();
    this.#disarm();

    if (!this.designMode) this.#teaserSessionDismissed(true);

    document.dispatchEvent(new CustomEvent('newsletter-popup:teaser-dismiss', { detail: { key: this.key } }));
  }

  /* -------------------------------------------------------------- Triggers */

  #armTriggers() {
    if (this.#armed) return;

    const hasDelay = this.dataset.triggerDelay === 'true';
    const hasScroll = this.dataset.triggerScroll === 'true';
    const hasExit = this.dataset.triggerExit === 'true';
    if (!hasDelay && !hasScroll && !hasExit) return;

    this.#armed = true;

    if (hasDelay) {
      const delay = parseFloat(this.dataset.delay || '5') * 1000;
      this.#timers.push(setTimeout(() => this.open(), Math.max(0, delay)));
    }

    if (hasScroll) {
      const target = parseInt(this.dataset.scroll || '40', 10);
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const doc = document.documentElement;
          const scrollable = doc.scrollHeight - window.innerHeight;
          if (scrollable <= 0) return;
          if ((window.scrollY / scrollable) * 100 >= target) this.open();
        });
      };
      this.#onTrigger(window, 'scroll', onScroll, { passive: true });
    }

    // Intention de sortie : desktop au pointeur fin uniquement (inopérant au tactile)
    if (hasExit && window.innerWidth > MOBILE_BREAKPOINT && window.matchMedia('(pointer: fine)').matches) {
      const onMouseOut = (event) => {
        if (event.relatedTarget || event.clientY > 0) return;
        this.open();
      };
      this.#onTrigger(document, 'mouseout', onMouseOut);
    }
  }

  /** Désarme les déclencheurs uniquement — les listeners de la modale restent actifs. */
  #disarm() {
    this.#timers.forEach(clearTimeout);
    this.#timers = [];
    this.#triggerListeners.forEach(([target, event, handler, options]) =>
      target.removeEventListener(event, handler, options)
    );
    this.#triggerListeners = [];
    this.#armed = false;
  }

  #teardown() {
    this.#disarm();
    this.#listeners.forEach(([target, event, handler, options]) => target.removeEventListener(event, handler, options));
    this.#listeners = [];
  }

  #on(target, event, handler, options) {
    target.addEventListener(event, handler, options);
    this.#listeners.push([target, event, handler, options]);
  }

  #onTrigger(target, event, handler, options) {
    target.addEventListener(event, handler, options);
    this.#triggerListeners.push([target, event, handler, options]);
  }

  /* --------------------------------------------------- Ouverture / fermeture */

  /** Une autre modale ou un tiroir du thème est-il déjà ouvert ? */
  #themeIsBusy() {
    if (document.body.classList.contains('overflow-hidden')) return true;
    return Array.from(document.querySelectorAll('dialog[open]')).some((dialog) => dialog !== this.dialog);
  }

  open() {
    if (this.#opened || !this.dialog || this.dialog.open) return;

    // Ne jamais passer par-dessus le panier tiroir, la recherche ou un autre popup
    if (this.#themeIsBusy()) {
      if (this.#busyRetries >= BUSY_MAX_RETRIES) return;
      this.#busyRetries += 1;
      this.#timers.push(setTimeout(() => this.open(), BUSY_RETRY_DELAY));
      return;
    }

    this.#opened = true;
    this.#disarm();
    this.#hideTeaser();

    this.dialog.showModal();
    this.dialog.scrollTop = 0;
    document.body.classList.add('overflow-hidden');
    this.#sessionSeen(true);

    document.dispatchEvent(new CustomEvent('newsletter-popup:open', { detail: { key: this.key } }));
  }

  close() {
    if (!this.dialog || !this.dialog.open) return;
    if (this.dialog.classList.contains('dialog-modal--closing')) return;

    this.dialog.classList.add('dialog-modal--closing');

    const finish = () => {
      this.dialog.removeEventListener('animationend', finish);
      clearTimeout(this.#closeFallback);
      this.dialog.classList.remove('dialog-modal--closing');
      this.dialog.close();
    };

    this.dialog.addEventListener('animationend', finish);
    this.#closeFallback = setTimeout(finish, this.#animationDuration() + 100);
  }

  #animationDuration() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--animation-speed-medium');
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return 250;
    return raw.includes('ms') ? value : value * 1000;
  }

  #releaseScroll() {
    // Ne libère le scroll que si aucune autre modale du thème n'est ouverte
    if (!document.querySelector('dialog[open]')) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  #bindDialog() {
    // Escape : le <dialog> gère nativement le focus, on ajoute juste l'animation
    this.#on(this.dialog, 'cancel', (event) => {
      if (this.dialog.classList.contains('dialog-modal--closing')) return;
      event.preventDefault();
      this.close();
    });

    this.#on(this.dialog, 'close', () => {
      this.#opened = false;
      this.#releaseScroll();

      if (!this.designMode && !this.showTeaser) this.#write({ closedAt: Date.now() });

      // Teaser visible après fermeture du popup (sauf croix teaser / inscription)
      if (this.showTeaser && !this.#teaserDismissed && this.#shouldShow()) {
        this.#showTeaser();
      }

      document.dispatchEvent(new CustomEvent('newsletter-popup:close', { detail: { key: this.key } }));
    });

    if (this.dataset.closeOnOverlay === 'true') {
      this.#on(this.dialog, 'click', (event) => {
        if (event.target !== this.dialog) return; // clic sur le contenu : on ignore
        const rect = this.dialog.getBoundingClientRect();
        const inside =
          rect.top <= event.clientY &&
          event.clientY <= rect.top + rect.height &&
          rect.left <= event.clientX &&
          event.clientX <= rect.left + rect.width;
        if (!inside) this.close();
      });
    }

    this.closeButtons.forEach((button) =>
      this.#on(button, 'click', (event) => {
        event.preventDefault();
        this.close();
      })
    );
  }

  /* ------------------------------------------------------------ Formulaire */

  #bindForm() {
    if (!this.form) return;

    // Envoi natif Shopify — on marque seulement la source pour rouvrir le popup au retour
    this.#on(this.form, 'submit', () => {
      this.#markPendingNativeSubmit();
    });
  }

  #markPendingNativeSubmit() {
    try {
      sessionStorage.setItem(this.key + ':pending', '1');
    } catch (error) {
      /* noop */
    }
  }

  /**
   * Après un envoi natif depuis le popup, Shopify recharge la page avec form.errors
   * ou form.posted_successfully? (parfois ?customer_posted=true). On rouvre le popup
   * uniquement si l'envoi venait du popup (pas du footer).
   */
  #handleNativeFormResponse() {
    let pending = false;

    try {
      pending = sessionStorage.getItem(this.key + ':pending') === '1';
      if (pending) sessionStorage.removeItem(this.key + ':pending');
    } catch (error) {
      return false;
    }

    const serverSuccess = this.form?.querySelector('.form-success');
    const serverError = this.form?.querySelector('.form-error');
    const url = new URL(window.location.href);
    const urlPosted = url.searchParams.get('customer_posted') === 'true';

    if (!pending && !serverSuccess && !serverError) return false;

    if (serverSuccess || (pending && urlPosted)) {
      if (urlPosted) {
        url.searchParams.delete('customer_posted');
        window.history.replaceState({}, '', url.toString());
      }

      this.#persistSubscribed();
      this.open();
      return true;
    }

    if (serverError && pending) {
      this.open();
      return true;
    }

    return false;
  }

  #persistSubscribed() {
    if (this.designMode) return;

    this.#write({ subscribed: true, subscribedAt: Date.now() });
    this.#teaserDismissed = true;
    this.#hideTeaser();
    document.dispatchEvent(new CustomEvent('newsletter-popup:subscribed', { detail: { key: this.key } }));
  }

  /* --------------------------------------------------------- Éditeur de thème */

  #bindDesignMode() {
    if (!this.designMode) return;

    const sectionId = this.dataset.sectionId;
    const isMine = (event) => event.detail && event.detail.sectionId === sectionId;

    this.#on(document, 'shopify:section:select', (event) => {
      if (isMine(event)) this.#forceOpen();
    });
    this.#on(document, 'shopify:block:select', () => this.#forceOpen());
    this.#on(document, 'shopify:section:deselect', (event) => {
      if (isMine(event)) this.close();
    });
  }

  #forceOpen() {
    this.#opened = false;
    this.#busyRetries = 0;
    this.open();
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
